import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from './tokenStorage';

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000/api/v1';

interface ApiEnvelope<T> {
  success: boolean;
  data: T | null;
  error: { code: string; message: string; details?: unknown } | null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Listener = () => void;
const onAuthLost = new Set<Listener>();

export function subscribirAuthLost(fn: Listener): () => void {
  onAuthLost.add(fn);
  return () => {
    onAuthLost.delete(fn);
  };
}

function emitirAuthLost(): void {
  onAuthLost.forEach((fn) => fn());
}

let refreshPromise: Promise<boolean> | null = null;

async function intentarRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const refreshToken = await getRefreshToken();
      if (!refreshToken) return false;

      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      // Solo un rechazo explícito de la credencial cierra la sesión. Un 429
      // (throttle de 30/min) o un 5xx/502 (nginx durante los ~2-4 min que dura un
      // deploy) son transitorios: conservar los tokens y dejar que el sync worker
      // reintente en 30s. Antes cualquier no-2xx borraba el refresh token del
      // disco, así que cada deploy deslogueaba al usuario en todos sus equipos.
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          await clearTokens();
          emitirAuthLost();
        } else {
          console.warn(`Refresh transitorio (HTTP ${res.status}); se conservan los tokens`);
        }
        return false;
      }
      const body = (await res.json()) as ApiEnvelope<{
        accessToken: string;
        refreshToken: string;
      }>;
      if (!body.success || !body.data?.accessToken || !body.data?.refreshToken) {
        await clearTokens();
        emitirAuthLost();
        return false;
      }
      // Disco ANTES que memoria, y como primera operación tras parsear el body:
      // el server ya rotó, así que cualquier cosa que muera entre la respuesta y
      // este write deja el token viejo (muerto) en disco para siempre.
      await setRefreshToken(body.data.refreshToken);
      setAccessToken(body.data.accessToken);
      return true;
    } catch (e) {
      console.error('Refresh falló:', e);
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | undefined>;
  signal?: AbortSignal;
  intentarRefresh?: boolean;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const intentar = options.intentarRefresh ?? true;
  const url = construirUrl(path, options.query);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  // Si el server responde 401, intentar refresh aunque no haya access token en
  // memoria. Esto cubre el caso de cargar la página: el access token vive en
  // memoria y se pierde al recargar, pero el refresh token persiste en
  // @capacitor/preferences. Sin esto, AuthProvider tira sesión válida.
  if (res.status === 401 && intentar) {
    const ok = await intentarRefresh();
    if (ok) {
      return apiFetch<T>(path, { ...options, intentarRefresh: false });
    }
    throw new ApiError(401, 'UNAUTHORIZED', 'Sesión expirada');
  }

  if (res.status === 204) {
    return undefined as T;
  }

  let body: ApiEnvelope<T> | null = null;
  try {
    body = (await res.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(res.status, 'INVALID_RESPONSE', `Respuesta no JSON (${res.status})`);
  }

  if (!res.ok || !body.success) {
    const err = body?.error ?? { code: 'UNKNOWN', message: `HTTP ${res.status}` };
    throw new ApiError(res.status, err.code, err.message, err.details);
  }
  return body.data as T;
}

function construirUrl(path: string, query?: Record<string, string | undefined>): string {
  const url = new URL(`${API_BASE}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== '') url.searchParams.set(k, v);
    }
  }
  return url.toString();
}

export async function healthCheck(): Promise<boolean> {
  try {
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 3000);
    const res = await fetch(`${API_BASE}/health`, { signal: ctrl.signal });
    clearTimeout(timeout);
    if (!res.ok) return false;
    const body = (await res.json()) as ApiEnvelope<{ status: string; db: string }>;
    return body.success && body.data?.status === 'ok' && body.data?.db === 'ok';
  } catch {
    return false;
  }
}

export const apiBaseUrl = API_BASE;
